import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/quizStepsConfig";
import type { AmountEditUnit } from "./amountInputFormat";
import {
  formatCompactCurrency,
  formatGroupedDollars,
} from "./amountInputFormat";
import type { GoalId, GoalResultVariant } from "./types";
import { GOAL_ID_TO_TYPE } from "@hooks/onboarding/mapGoalIdToGoalType";

const I18n = I18nUtil.createScoped(Translations);

type QuizQuestionKind = "name" | "choice" | "numeric";

export interface QuizStepDefinition {
  stepId:
    | "quiz/name"
    | "quiz/age"
    | "quiz/income"
    | "quiz/expenses"
    | "quiz/cash"
    | "quiz/debt"
    | "quiz/invest"
    | "quiz/risk"
    | "quiz/jobcat"
    | "quiz/jobrole";
  figmaNodeIds: string[];
  kind: QuizQuestionKind;
  question: string;
  subtitle?: string;
  /** Ed bubble copy for numeric steps (Figma per-question message). */
  heroMessage?: string | ((displayName: string) => string);
  options?: { id: string; label: string }[];
  numeric?: {
    min: number;
    max: number;
    step: number;
    defaultValue: number;
    /** When true, min (typically 0) is a valid answer; other numeric steps require value > min. */
    allowZero?: boolean;
    prefix?: string;
    suffix?: string;
    format?: (n: number) => string;
    /**
     * Opt-in editable amount input. Unset → read-only display (cash/debt/invest).
     * `k` = focus shows thousands; `dollar` = focus shows full dollars.
     */
    editUnit?: AmountEditUnit;
    /** 滑杆下方两端标签覆盖（默认 "$0" / formatSliderCapLabel(max)）— 年龄题用 "18" / "60+" */
    minLabel?: string;
    maxLabel?: string;
    /** 刻度类型：nonlinear（默认，金额题低值精控）| linear（均匀刻度，年龄） */
    scale?: "linear" | "nonlinear";
    /**
     * 手输框上界，与滑杆 `max` **解耦**（§1.4.2-A1）。缺省时退化为 `max`（= 改造前行为）。
     *
     * 为什么要这个：改造前 clamp 用的就是滑杆 `max`，于是 `max=100k` 时用户想手输
     * `1000`（k）会在敲到第 4 位时被 draft 回写成 `100` —— **打不进去且零提示**。
     * 不设「无上界」是防手滑输入超长数字打爆后端公式。
     */
    inputMax?: number;
  };
  answerKey:
    | "name"
    | "age"
    | "income"
    | "expenses"
    | "cash"
    | "debt"
    | "invest"
    | "risk"
    | "jobCategory"
    | "jobRole";
}

const QUIZ_NAME_SUBTITLE = I18n.t("I'm Ed — your money person.");

const RISK_OPTIONS = [
  { id: "safe", label: I18n.t("Play it safe") },
  { id: "balanced", label: I18n.t("Balanced") },
  { id: "growth", label: I18n.t("Go for growth") },
];

export const QUIZ_STEP_DEFINITIONS: QuizStepDefinition[] = [
  {
    stepId: "quiz/name",
    figmaNodeIds: ["96121:8430", "96121:8432"],
    kind: "name",
    question: I18n.t("What should I call you?"),
    subtitle: QUIZ_NAME_SUBTITLE,
    answerKey: "name",
  },
  {
    stepId: "quiz/age",
    // 验收 P0-1：精确整数滑杆（18–60，60 代表 60+）；无 $ 前缀，下方标签 18 / 60+
    figmaNodeIds: ["96121:8433", "96121:8435"],
    kind: "numeric",
    question: I18n.t("How old are you?"),
    answerKey: "age",
    numeric: {
      min: 18,
      max: 60,
      step: 1,
      defaultValue: 18,
      suffix: I18n.t("years old"),
      minLabel: "18",
      maxLabel: "60+",
      scale: "linear",
    },
  },
  {
    stepId: "quiz/income",
    figmaNodeIds: ["102803:55987", "102839:82775"],
    kind: "numeric",
    question: I18n.t("About how much do you earn?"),
    heroMessage: I18n.t(
      "Round numbers are fine — I'll sharpen them as I get to know you.",
    ),
    numeric: {
      min: 0,
      max: 1_000_000,
      inputMax: 10_000_000,
      step: 5_000,
      defaultValue: 0,
      allowZero: true,
      editUnit: "k",
      prefix: "$",
      suffix: I18n.t("/ year"),
      format: (value) => (value === 0 ? "0" : formatCompactCurrency(value)),
    },
    answerKey: "income",
  },
  {
    stepId: "quiz/expenses",
    figmaNodeIds: ["102803:56029", "102839:82815"],
    kind: "numeric",
    question: I18n.t("And how much goes out a month?"),
    heroMessage: I18n.t("Got the top line. Now what goes out — roughly."),
    numeric: {
      min: 0,
      max: 50_000,
      inputMax: 500_000,
      step: 100,
      defaultValue: 0,
      allowZero: true,
      editUnit: "dollar",
      prefix: "$",
      suffix: I18n.t("/ month"),
      format: formatGroupedDollars,
    },
    answerKey: "expenses",
  },
  {
    stepId: "quiz/cash",
    figmaNodeIds: ["102803:56113", "102839:82855"],
    kind: "numeric",
    question: I18n.t("What's in the bank?"),
    heroMessage: I18n.t(
      "To get this right I just need one number: your everyday cash, sitting in checking and savings. Just drag.",
    ),
    numeric: {
      min: 0,
      max: 500_000,
      inputMax: 10_000_000,
      step: 1_000,
      defaultValue: 0,
      allowZero: true,
      editUnit: "k",
      prefix: "$",
      suffix: I18n.t("total"),
      format: (value) => (value === 0 ? "0" : formatCompactCurrency(value)),
    },
    answerKey: "cash",
  },
  {
    stepId: "quiz/debt",
    figmaNodeIds: ["102803:56071", "102839:82898"],
    kind: "numeric",
    question: I18n.t("What do you owe?"),
    heroMessage: I18n.t(
      "Now the other side of the ledger — no judgment, it helps me plan.",
    ),
    numeric: {
      min: 0,
      max: 1_000_000,
      inputMax: 10_000_000,
      step: 1_000,
      defaultValue: 0,
      allowZero: true,
      editUnit: "k",
      prefix: "$",
      suffix: I18n.t("total"),
      format: (value) => (value === 0 ? "0" : formatCompactCurrency(value)),
    },
    answerKey: "debt",
  },
  {
    stepId: "quiz/invest",
    figmaNodeIds: ["102803:56157", "102839:82938"],
    kind: "numeric",
    question: I18n.t("How much have you got invested?"),
    heroMessage: I18n.t(
      "Last piece — what's already invested and working for you.",
    ),
    numeric: {
      min: 0,
      max: 2_000_000,
      inputMax: 10_000_000,
      step: 5_000,
      defaultValue: 0,
      allowZero: true,
      editUnit: "k",
      prefix: "$",
      suffix: I18n.t("invested"),
      format: (value) => (value === 0 ? "0" : formatCompactCurrency(value)),
    },
    answerKey: "invest",
  },
  {
    stepId: "quiz/risk",
    figmaNodeIds: ["96121:8442"],
    kind: "choice",
    question: I18n.t("What's your style with money?"),
    heroMessage: (name) =>
      I18n.t(
        "That's your whole money picture, %{var0}. One last thing — about you, not your accounts.",
        { var0: name || "there" },
      ),
    options: RISK_OPTIONS,
    answerKey: "risk",
  },
  {
    stepId: "quiz/jobcat",
    figmaNodeIds: ["108867:131292", "108867:182264"],
    kind: "choice",
    question: I18n.t("Which best describes your work?"),
    // R16 = 一律取 PRD：Figma `108867:131314` 写的是
    // 「…pick the line of work that fits best.」，不采纳
    heroMessage: I18n.t(
      "Now, what do you do? Start broad — the exact role comes next.",
    ),
    // options 不写死在这里 —— 19 大类来自 `occupations.json`，是**数据**不是文案，
    // 由 Shell 调 `resolveQuizOptions()` 在渲染时注入
    answerKey: "jobCategory",
  },
  {
    stepId: "quiz/jobrole",
    figmaNodeIds: ["108867:131380", "108867:182349"],
    kind: "choice",
    question: I18n.t("And your role?"),
    // R16 = 一律取 PRD。这句里的「Not listed? Pick the closest.」是兜底项
    // （App `108867:131440` 的 OptionRow/Fallback，AC27 裁定两端都不做）被裁掉后
    // **唯一的出口提示**，不能丢
    heroMessage: I18n.t("Now the specific role. Not listed? Pick the closest."),
    // options 取决于 UI2 选了哪个大类 → 同样运行时合成
    answerKey: "jobRole",
  },
];

/** Resolve per-step Ed hero copy (string or name-aware function). */
export function resolveQuizHeroMessage(
  definition: Pick<QuizStepDefinition, "heroMessage">,
  displayName: string,
  fallback: (name: string) => string,
): string {
  if (typeof definition.heroMessage === "function") {
    return definition.heroMessage(displayName);
  }
  if (definition.heroMessage) {
    return definition.heroMessage;
  }
  return fallback(displayName);
}

/** V3.1.3 Chat 要数据槽的题组类目（EDG-591，PRD 附录 A.1 `category` 列） */
export type ConnectQuestionCategory = "invest" | "banking" | "both";

export interface ConnectQuestion {
  /** 英文原句：埋点恒传此值 */
  prompt: string;
  /** 调用时按当前 locale 取译文（展示与发送用） */
  label: () => string;
}

export interface ConnectQuestionGroup {
  questions: readonly [ConnectQuestion, ConnectQuestion];
  category: ConnectQuestionCategory;
}

const CONNECT_QUESTIONS_PORTFOLIO: ConnectQuestionGroup = {
  questions: [
    {
      prompt: "How risky is my portfolio right now?",
      label: () => I18n.t("How risky is my portfolio right now?"),
    },
    {
      prompt: "How long until I hit my first $1M, for real?",
      label: () => I18n.t("How long until I hit my first $1M, for real?"),
    },
  ],
  category: "invest",
};

/** emergency 只来自广告漏斗回填 / legacy runway，不在 GOAL_OPTIONS 里，单独一组 */
export const CONNECT_QUESTIONS_EMERGENCY: ConnectQuestionGroup = {
  questions: [
    {
      prompt: "How many months could I last if income stopped?",
      label: () => I18n.t("How many months could I last if income stopped?"),
    },
    {
      prompt: "Is my emergency fund actually enough?",
      label: () => I18n.t("Is my emergency fund actually enough?"),
    },
  ],
  category: "banking",
};

export interface GoalOptionDefinition {
  id: GoalId;
  category: string;
  figmaNodeId: string;
  resultVariant: GoalResultVariant;
  icon: string;
  title: string;
  needsAccountHint?: boolean;
  /** V3.1.3 Chat 要数据槽题组（EDG-591） */
  connectQuestions?: ConnectQuestionGroup;
}

export const GOAL_OPTIONS: GoalOptionDefinition[] = [
  {
    id: "money-leak",
    category: I18n.t("Spending"),
    figmaNodeId: "102803:53972",
    resultVariant: "money-leak",
    // R9: Figma `finance-growth` is not in iconfont yet — reuse `finance`.
    icon: "finance",
    title: I18n.t("Fix my cash leak"),
    connectQuestions: {
      questions: [
        {
          prompt: "What am I actually spending each month?",
          label: () => I18n.t("What am I actually spending each month?"),
        },
        {
          prompt: "Which recurring charges are quietly draining me?",
          label: () =>
            I18n.t("Which recurring charges are quietly draining me?"),
        },
      ],
      category: "banking",
    },
  },
  {
    id: "idle-cash",
    category: I18n.t("Cash Yield"),
    figmaNodeId: "102803:53982",
    resultVariant: "idle-cash",
    icon: "dollar-bag",
    title: I18n.t("Get my money earning more"),
    connectQuestions: {
      questions: [
        {
          prompt: "How much of my cash is just sitting there?",
          label: () => I18n.t("How much of my cash is just sitting there?"),
        },
        {
          prompt: "What is my idle cash costing me this year?",
          label: () => I18n.t("What is my idle cash costing me this year?"),
        },
      ],
      category: "banking",
    },
  },
  {
    id: "portfolio-risk",
    category: I18n.t("Invest"),
    figmaNodeId: "102803:53990",
    resultVariant: "portfolio-risk",
    icon: "check-shield",
    title: I18n.t("Check my portfolio risk"),
    needsAccountHint: true,
    connectQuestions: {
      questions: [
        {
          prompt: "Is one stock running too much of my portfolio?",
          label: () => I18n.t("Is one stock running too much of my portfolio?"),
        },
        {
          prompt: "Does my portfolio match the risk I said I can take?",
          label: () =>
            I18n.t("Does my portfolio match the risk I said I can take?"),
        },
      ],
      category: "invest",
    },
  },
  {
    id: "debt-free",
    category: I18n.t("Debt"),
    figmaNodeId: "102803:53998",
    resultVariant: "debt-free",
    icon: "dollar-bag",
    title: I18n.t("Plan my way out of debt"),
    connectQuestions: {
      questions: [
        {
          prompt: "How much is my debt really costing me each month?",
          label: () =>
            I18n.t("How much is my debt really costing me each month?"),
        },
        {
          prompt: "When will I really be debt-free?",
          label: () => I18n.t("When will I really be debt-free?"),
        },
      ],
      category: "banking",
    },
  },
  {
    id: "million",
    category: I18n.t("Wealth"),
    figmaNodeId: "102803:54006",
    resultVariant: "million",
    icon: "finance",
    title: I18n.t("Build my first $1M"),
    connectQuestions: CONNECT_QUESTIONS_PORTFOLIO,
  },
  {
    id: "home",
    category: I18n.t("Home"),
    figmaNodeId: "102803:54014",
    resultVariant: "home",
    // R9: Figma `sofa` is not in iconfont yet — reuse `home`.
    icon: "home",
    title: I18n.t("See what home I can afford"),
    connectQuestions: {
      questions: [
        {
          prompt: "How much house can I really afford?",
          label: () => I18n.t("How much house can I really afford?"),
        },
        {
          prompt: "How far am I from a down payment?",
          label: () => I18n.t("How far am I from a down payment?"),
        },
      ],
      category: "banking",
    },
  },
  {
    id: "retirement",
    category: I18n.t("Financial Freedom"),
    figmaNodeId: "102803:54022",
    resultVariant: "retirement",
    // R9: Figma `briefcase-alt` is not in iconfont yet — reuse `goal`.
    icon: "goal",
    title: I18n.t("Make work optional sooner"),
    connectQuestions: CONNECT_QUESTIONS_PORTFOLIO,
  },
];

export const getGoalOption = (
  goalId: GoalId,
): GoalOptionDefinition | undefined =>
  GOAL_OPTIONS.find((option) => option.id === goalId);

/**
 * onboarding goalType → Chat 要数据槽题组；emergency 单独一组。
 * 没有 onboarding goal / 未知 goalType → undefined，不填槽（PRD 附录 A.1，2026-10-21）。
 */
export const getConnectQuestions = (
  goalType?: string | null,
): ConnectQuestionGroup | undefined =>
  GOAL_OPTIONS.find((option) => GOAL_ID_TO_TYPE[option.id] === goalType)
    ?.connectQuestions ??
  (goalType === "emergency" ? CONNECT_QUESTIONS_EMERGENCY : undefined);

export const RESULT_VARIANT_CONFIG: Record<
  GoalResultVariant,
  {
    figmaNodeId: string;
    bubbleLabel: string;
    bubbleQuestion: string;
    heroLine: string;
    heroValue: string;
    heroSubline: string;
    payoffTitle: string;
    payoffBody: string;
    cta: string;
  }
> = {
  million: {
    figmaNodeId: "102803:54961",
    bubbleLabel: I18n.t("You'll hit your $1M!"),
    bubbleQuestion: I18n.t("Same income, faster line."),
    heroLine: I18n.t("On your current pace, here's the year."),
    heroValue: "2043",
    heroSubline: I18n.t("that's $1,000,000 — around age 57."),
    payoffTitle: I18n.t("5 years sooner with Ed"),
    payoffBody: I18n.t("Ed runs the moves for you, automatically."),
    cta: I18n.t("Have Ed track this"),
  },
  home: {
    figmaNodeId: "102803:55315",
    bubbleLabel: I18n.t("What home could you afford?"),
    bubbleQuestion: I18n.t("Here's your result"),
    heroLine: I18n.t("On your numbers today, here's your ceiling."),
    heroValue: "$420k",
    heroSubline: I18n.t("with your ~$84,000 deposit and current income."),
    payoffTitle: I18n.t("Ed extends budget by $80k for a larger home"),
    payoffBody: I18n.t(
      "Ed lifts your buying power without overstretching you.",
    ),
    cta: I18n.t("Have Ed track this"),
  },
  runway: {
    figmaNodeId: "96138:32116",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("How long could you last with no income?"),
    heroLine: I18n.t("If income stopped today, here's how long."),
    heroValue: "4 months",
    heroSubline: I18n.t("that's about $13,000 in the bank — gone by October."),
    payoffTitle: I18n.t("5 months longer with Ed"),
    payoffBody: I18n.t(
      "He frees up idle cash and builds your buffer, automatically.",
    ),
    cta: I18n.t("Have Ed track this"),
  },
  "idle-cash": {
    figmaNodeId: "102803:54293",
    bubbleLabel: I18n.t("Your cash can work harder."),
    bubbleQuestion: I18n.t("Same money, same access."),
    heroLine: I18n.t("What you could earn in 15 years"),
    heroValue: "$34,000",
    heroSubline: I18n.t("that's $1,600 every month"),
    payoffTitle: I18n.t("$34k more in 15 years, hands-off"),
    payoffBody: I18n.t("Same money, same access — just not in a 0.5% account."),
    cta: I18n.t("Have Ed track this"),
  },
  retirement: {
    figmaNodeId: "102803:55138",
    bubbleLabel: I18n.t("When work is a choice."),
    bubbleQuestion: I18n.t("Most people never work this out."),
    heroLine: I18n.t("When work becomes optional"),
    heroValue: "Age 54",
    heroSubline: I18n.t("that's $2.1M. About 25x what you spend in a year"),
    payoffTitle: I18n.t("5 years sooner with Ed"),
    payoffBody: I18n.t("Ed orders your money to bring the date forward."),
    cta: I18n.t("Have Ed track this"),
  },
  "debt-free": {
    figmaNodeId: "102803:54781",
    bubbleLabel: I18n.t("You're closer than it feels."),
    bubbleQuestion: I18n.t("Same payment, faster out."),
    heroLine: I18n.t("How fast you could be debt-free"),
    heroValue: "14 months",
    heroSubline: I18n.t("and about $4,200 less interest"),
    payoffTitle: I18n.t("14 months sooner with Ed"),
    payoffBody: I18n.t(
      "Same money each month — just in the right order, with your rate watched.",
    ),
    cta: I18n.t("Have Ed track this"),
  },
  "money-leak": {
    figmaNodeId: "102803:54111",
    bubbleLabel: I18n.t("Your spending has room."),
    bubbleQuestion: I18n.t("Same life, more kept."),
    heroLine: I18n.t("What you could save yearly"),
    heroValue: "$15,120",
    heroSubline: I18n.t("that's $1,260 per month"),
    payoffTitle: I18n.t("Ed reminds you every payday"),
    payoffBody: I18n.t("Maps each bucket, then checks it held next month."),
    cta: I18n.t("Have Ed track this now"),
  },
  "portfolio-risk": {
    figmaNodeId: "102803:54579",
    bubbleLabel: I18n.t("Your portfolio can steady out."),
    bubbleQuestion: I18n.t("Same holdings, less riding on one."),
    heroLine: I18n.t("Sector Diversification"),
    heroValue: "34%",
    heroSubline: I18n.t("Common guardrail: no single sector above %{var0}%", {
      var0: 15,
    }),
    payoffTitle: I18n.t("$31,000 less lost in a 2022-style drop"),
    payoffBody: I18n.t("Spread so one sector can't decide your year."),
    cta: I18n.t("Have Ed track this"),
  },
};

export const PLAN_MOVES = [
  {
    number: 1,
    title: I18n.t("Put your idle cash to work"),
    tag: "+$1,600/yr",
    body: I18n.t("≈$40k sitting idle → a 4.5% position."),
    locked: false,
  },
  {
    number: 2,
    title: I18n.t("Lock a sharper mortgage rate"),
    tag: "+$40k home",
    body: I18n.t("Your refinance window is open right now."),
    locked: false,
  },
  {
    number: 3,
    title: I18n.t("Rebalance to reach $1M sooner"),
    tag: "~3 yrs sooner",
    body: I18n.t("Shift your mix so the milestone arrives years earlier."),
    locked: true,
  },
  {
    number: 4,
    title: I18n.t("A tax move you're missing"),
    tag: "+$2,300/yr",
    body: I18n.t("A simple shift that quietly hands you back money."),
    locked: true,
  },
  {
    number: 5,
    title: I18n.t("Build your safety cushion"),
    tag: I18n.t("Peace of mind"),
    body: I18n.t("Six months of breathing room, set on autopilot."),
    locked: true,
  },
] as const;
